import React, { useState, useEffect, useRef } from 'react';
import ChatEmbedWidget from './ChatEmbedWidget';
import { MessageSquare, MessageCircle, Bot, Headphones, User, Minimize2 } from 'lucide-react';
import api from '../api';
import {
    restoreLauncherState,
    saveLauncherState,
    readSessionId,
    noteLeft,
    clearAll,
    resetVerdict,
} from './chatSessionStore';

// Launcher size options — px values match ComboWidgetV2's launcher size scale.
// 'medium' (56px) matches the button's original fixed w-14/h-14 size, so agents
// that never set chat_button_size keep their exact existing appearance.
const BUTTON_SIZES = { small: 44, medium: 56, large: 68 };

const ChatEmbedButton = ({ agentId }) => {
    // This widget lives in an iframe on the customer's site, so every page the
    // visitor clicks through to remounts it from zero. Reopening onto the live
    // conversation is what makes a chat survive that navigation - otherwise the
    // visitor came back to a closed launcher and read it as the chat having
    // ended. Resolved once per mount; see chatSessionStore for the resume window.
    const restoredUi = useRef(null);
    if (restoredUi.current === null) restoredUi.current = restoreLauncherState(agentId);

    const [isOpen, setIsOpen] = useState(restoredUi.current.isOpen);
    const [isMinimized, setIsMinimized] = useState(false);
    const [agent, setAgent] = useState(null);
    const [agentLoaded, setAgentLoaded] = useState(false);
    const widgetKeyRef = useRef(0); // Key to preserve widget state
    const engagedRef = useRef(restoredUi.current.isOpen);
    const [themeSettings, setThemeSettings] = useState({
        icon: 'message-square',
        buttonColor: '#6366f1',
        position: 'bottom-right',
        primaryColor: '#6366f1',
        mode: 'light',
        logoUrl: null,
        buttonSize: 'medium',
        teaserEnabled: false,
        teaserMessage: "Need help? We're online now.",
        teaserBgColor: '#ffffff',
        teaserTextColor: '#111827',
        glowEnabled: false,
    });

    // Keep the stored launcher state in step with the live one so the next page
    // can restore it. hasStarted is always true here: unlike the combo widget
    // there is no channel picker, so opening this launcher is itself starting the
    // chat. Nothing is written until the visitor actually opens it, so a passerby
    // who never touches the widget leaves no trace in their browser.
    useEffect(() => {
        if (!agentId) return;
        if (isOpen) engagedRef.current = true;
        if (!engagedRef.current) return;
        saveLauncherState(agentId, { isOpen, hasStarted: true, channel: 'chat' });
    }, [agentId, isOpen]);

    // pagehide also fires when this frame is unloaded by the host page
    // navigating, which is the case the resume mechanism exists for.
    useEffect(() => {
        if (!agentId) return;
        const handleLeave = () => noteLeft(agentId);
        window.addEventListener('pagehide', handleLeave);
        window.addEventListener('beforeunload', handleLeave);
        return () => {
            window.removeEventListener('pagehide', handleLeave);
            window.removeEventListener('beforeunload', handleLeave);
        };
    }, [agentId]);

    // Load agent details and theme settings
    useEffect(() => {
        if (agentId) {
            api.get(`/agents/${agentId}`)
                .then(response => {
                    setAgent(response.data);

                    // Apply theme settings from agent or URL params
                    const params = new URLSearchParams(window.location.search);
                    const urlIcon = params.get('icon');
                    const urlBtnColor = params.get('btnColor');
                    const urlPosition = params.get('position');
                    const urlPrimary = params.get('primary');
                    const urlTheme = params.get('theme');

                    setThemeSettings({
                        icon: urlIcon || response.data.chat_button_icon || 'message-square',
                        buttonColor: urlBtnColor ? `#${urlBtnColor}` : response.data.chat_button_color || '#6366f1',
                        position: urlPosition || response.data.chat_button_position || 'bottom-right',
                        primaryColor: urlPrimary ? `#${urlPrimary}` : response.data.chat_primary_color || '#6366f1',
                        mode: urlTheme || response.data.chat_theme_mode || 'light',
                        logoUrl: response.data.chat_button_logo_url || null,
                        buttonSize: response.data.chat_button_size || 'medium',
                        teaserEnabled: response.data.chat_teaser_enabled || false,
                        teaserMessage: response.data.chat_teaser_message || "Need help? We're online now.",
                        teaserBgColor: response.data.chat_teaser_bg_color || '#ffffff',
                        teaserTextColor: response.data.chat_teaser_text_color || '#111827',
                        glowEnabled: response.data.chat_glow_enabled || false,
                    });
                })
                .catch(err => console.error('Failed to load agent:', err))
                .finally(() => setAgentLoaded(true));
        }
    }, [agentId]);

    // Don't reset widget key when minimizing/maximizing - preserve state
    const handleMinimizeToggle = () => {
        setIsMinimized(!isMinimized);
        // Don't reset widgetKeyRef - keep the same widget instance
    };

    // Get the icon component based on theme settings
    const getIconComponent = () => {
        switch (themeSettings.icon) {
            case 'message-circle':
                return MessageCircle;
            case 'bot':
                return Bot;
            case 'headphones':
                return Headphones;
            case 'user':
                return User;
            default:
                return MessageSquare;
        }
    };

    // Rendered either inside the "?view=button" embed iframe (see
    // ChatEmbedPage) or, when an admin hits "Preview", directly in a
    // top-level tab. When embedded, the screen-edge margin is applied by the
    // parent page's loader script, which resizes/repositions the iframe
    // itself to match this widget's real footprint (see the postMessage
    // effect below) - so here we sit flush against our own iframe's edges
    // (margin 0). When not embedded, there is no outer script to add a
    // margin, so we apply the same 1.5rem/1rem margins this component used
    // before the resize-to-fit iframe behavior existed.
    const isEmbedded = typeof window !== 'undefined' && window.parent !== window;
    const getPositionStyles = (margin) => {
        const positions = {
            'bottom-right': { bottom: margin, right: margin },
            'bottom-left': { bottom: margin, left: margin },
            'top-right': { top: margin, right: margin },
            'top-left': { top: margin, left: margin },
        };
        return positions[themeSettings.position] || positions['bottom-right'];
    };

    const ButtonIcon = getIconComponent();
    const positionStyles = getPositionStyles(isEmbedded ? 0 : '1.5rem');
    const panelPositionStyles = getPositionStyles(isEmbedded ? 0 : '1rem');
    const isDarkMode = themeSettings.mode === 'dark' || (themeSettings.mode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Transparent "bleed" around the closed launcher. The iframe hugs the
    // widget's layout box, but the teaser's drop shadow (~15px) and the glow
    // ring (a scale transform, invisible to layout) render OUTSIDE that box and
    // would be clipped by the iframe edge. Padding the container by this amount
    // gives them room inside the iframe. Only needed when embedded (in preview
    // there's no clipping iframe). The parent's corner margin is reduced by the
    // same amount below so the button still sits ~24px from the screen corner.
    const closedButtonPx = BUTTON_SIZES[themeSettings.buttonSize] || BUTTON_SIZES.medium;
    const closedBleed = isEmbedded
        ? (themeSettings.glowEnabled ? Math.ceil(closedButtonPx * 0.45) : 16)
        : 0;
    const closedMargin = Math.max(4, 24 - closedBleed);

    // `pointer:coarse` reflects the real input hardware (unaffected by this
    // iframe's own size), so it's a reliable phone/tablet check. Touch devices
    // just get a slightly narrower panel (320 vs 384) below; the panel still
    // anchors to the bottom-right corner exactly like desktop rather than
    // centering on screen.
    const isTouchDevice = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const isCentered = false;

    // Report our real rendered size to the parent page so its loader script
    // can shrink/grow and reposition the iframe to match exactly - a
    // fixed-size iframe would otherwise sit over the page (blocking clicks
    // and :hover on whatever is underneath) even while the widget is closed.
    const containerRef = useRef(null);
    useEffect(() => {
        if (!isEmbedded) return;
        const el = containerRef.current;
        if (!el) return;

        // Closed launcher: the corner gap is provided partly by the container's
        // own bleed padding, so the parent margin is reduced by that amount.
        // Open panel: 1rem; minimized strip: 1.5rem.
        const marginPx = !isOpen ? closedMargin : (isMinimized ? 24 : 16);

        const sendSize = () => {
            // Use offsetWidth/Height (untransformed layout box), NOT
            // getBoundingClientRect (which includes transforms). The open panel
            // animates in via slideInUp (scale 0.95 -> 1), so measuring the
            // rect mid-animation reported a too-small size that the parent
            // locked the iframe to - the panel then finished growing past the
            // undersized iframe and got clipped at the top/left on first open,
            // only correcting after a later re-render (e.g. minimize/restore).
            window.parent.postMessage({
                type: 'braincx-chat-embed-resize',
                width: Math.ceil(el.offsetWidth),
                height: Math.ceil(el.offsetHeight),
                margin: marginPx,
                position: themeSettings.position,
                centered: isCentered,
            }, '*');
        };

        sendSize();
        const observer = new ResizeObserver(sendSize);
        observer.observe(el);
        return () => observer.disconnect();
    }, [isOpen, isMinimized, isCentered, agentLoaded, themeSettings.position, themeSettings.teaserEnabled, themeSettings.buttonSize, themeSettings.glowEnabled, closedBleed, closedMargin]);

    if (!isOpen) {
        const buttonPx = BUTTON_SIZES[themeSettings.buttonSize] || BUTTON_SIZES.medium;

        // Still fetching the agent's real icon/color - show a neutral skeleton
        // instead of the hardcoded default icon/color so nothing has to "swap"
        // once the agent loads (that swap is what reads as a flicker).
        if (!agentLoaded) {
            return (
                <div ref={containerRef} className="fixed z-[9999]" style={{ ...positionStyles, padding: closedBleed }}>
                    <style>{`
                        @keyframes chatEmbedLauncherPulse {
                            0%, 100% { opacity: 0.5; }
                            50% { opacity: 0.9; }
                        }
                    `}</style>
                    <div
                        className="rounded-full shadow-lg"
                        style={{
                            width: buttonPx,
                            height: buttonPx,
                            backgroundColor: '#d1d5db',
                            animation: 'chatEmbedLauncherPulse 1.1s ease-in-out infinite',
                        }}
                        aria-hidden="true"
                    />
                </div>
            );
        }

        // Floating button - closed state.
        // `w-max` (width: max-content) is critical when embedded: the loader
        // script starts the iframe at 64px, and a shrink-to-fit fixed element
        // would wrap the teaser to that 64px, then report the squished width
        // back to the parent - a feedback loop that pins the iframe at ~64px
        // and never lets the teaser expand. max-content measures the widget's
        // natural footprint (teaser up to 210px, button 64px) regardless of
        // the current iframe size, so the parent grows the iframe to match.
        return (
            <div ref={containerRef} className="fixed z-[9999] flex flex-col items-end gap-2 w-max" style={{ ...positionStyles, padding: closedBleed }}>
                <style>{`
                    @keyframes buttonPop {
                        0% {
                            transform: scale(0.8);
                            opacity: 0;
                        }
                        50% {
                            transform: scale(1.1);
                        }
                        100% {
                            transform: scale(1);
                            opacity: 1;
                        }
                    }
                    .chat-button-enter {
                        animation: buttonPop 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    }
                    @keyframes chatEmbedGlowRing {
                        0% { transform: scale(0.85); opacity: 0.55; }
                        100% { transform: scale(2.1); opacity: 0; }
                    }
                    @keyframes chatEmbedTeaserFloat {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-6px); }
                    }
                `}</style>
                {themeSettings.teaserEnabled && (
                    <div
                        className="px-4 py-3 rounded-2xl text-sm shadow-lg max-w-[210px]"
                        style={{
                            backgroundColor: themeSettings.teaserBgColor,
                            color: themeSettings.teaserTextColor,
                            border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
                            borderBottomRightRadius: 4,
                            animation: 'chatEmbedTeaserFloat 4s ease-in-out infinite',
                        }}
                    >
                        {themeSettings.teaserMessage}
                    </div>
                )}
                <div style={{ position: 'relative', width: buttonPx, height: buttonPx }}>
                    {themeSettings.glowEnabled && (
                        <span
                            className="pointer-events-none"
                            style={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                background: themeSettings.buttonColor,
                                animation: 'chatEmbedGlowRing 2.4s ease-out infinite',
                            }}
                        />
                    )}
                    <button
                        onClick={() => {
                            setIsOpen(true);
                            setIsMinimized(false);
                            // Only reset widget key when opening fresh (not when reopening)
                            if (widgetKeyRef.current === 0) {
                                widgetKeyRef.current = Date.now();
                            }
                        }}
                        className="chat-button-enter relative rounded-full transition-all duration-300 flex items-center justify-center group hover:scale-110 active:scale-95"
                        style={{
                            width: buttonPx,
                            height: buttonPx,
                            backgroundColor: themeSettings.buttonColor,
                            color: '#ffffff'
                        }}
                        aria-label="Open chat"
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                        {themeSettings.logoUrl ? (
                            <img
                                src={themeSettings.logoUrl}
                                alt=""
                                style={{ width: '60%', height: '60%', objectFit: 'contain' }}
                            />
                        ) : (
                            <ButtonIcon className="w-6 h-6 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
                        )}
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 animate-pulse" style={{
                            backgroundColor: '#10b981',
                            borderColor: isDarkMode ? '#1f2937' : '#ffffff'
                        }}></span>
                    </button>
                </div>
            </div>
        );
    }

    // Chat window - open state. Fixed pixel size - deliberately NOT expressed
    // via calc(100vw/100vh), since those would resolve against this iframe's
    // OWN viewport, which the parent page sizes based on what we report here.
    // Viewport clamping against the real screen happens in the parent's
    // loader script instead (see postMessage effect above), which has
    // genuine access to the host page's actual window dimensions.
    const expandedHeight = isMinimized ? 320 : 520;
    const expandedWidth = isMinimized ? 320 : (isTouchDevice ? 320 : 384);
    const containerStyle = {
        ...(isMinimized ? positionStyles : panelPositionStyles),
        width: expandedWidth,
        // Pre-chat form and chat interface share the same fixed panel height.
        // The form fills this height and scrolls internally only if it truly
        // overflows - previously it used fit-to-content sizing, which combined
        // with the form's own max-h-[75vh] to always show a scrollbar on a
        // short panel.
        ...(!isMinimized ? { height: expandedHeight, maxHeight: expandedHeight } : {}),
    };
    return (
        <div
            ref={containerRef}
            className={`fixed z-[9999] transition-all duration-500 ease-out ${
                isMinimized ? 'h-16' : 'chat-embed-window'
            }`}
            style={{
                ...containerStyle,
                transition: 'height 0.3s ease, max-height 0.3s ease, width 0.3s ease',
                // Always use the clean slideInUp (rise + fade). The mobile
                // "centered" case is handled by the parent centering the whole
                // iframe - the panel itself still fills its iframe anchored
                // bottom-right, so a translate(-50%,-50%) animation would shove
                // it out of the iframe and clip it mid-animation.
                animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                transformOrigin: 'bottom right'
            }}
        >
            <style>{`
                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>
            <div className="rounded-2xl flex flex-col overflow-hidden h-full" style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`
            }}>
                {/* Header. Minimized, the whole bar is the maximize control: the bar
                    IS the widget at that point, and maximizing is the only thing it
                    does, so a 16px icon in the corner is a needlessly small target. */}
                <div
                    className={`text-white p-4 flex items-center justify-between rounded-t-2xl flex-shrink-0 ${isMinimized ? 'cursor-pointer' : ''}`}
                    style={{ backgroundColor: themeSettings.buttonColor, transition: 'filter 0.15s ease' }}
                    {...(isMinimized
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            'aria-label': 'Maximize chat',
                            onClick: handleMinimizeToggle,
                            onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleMinimizeToggle();
                                }
                            },
                            onMouseEnter: (e) => { e.currentTarget.style.filter = 'brightness(1.08)'; },
                            onMouseLeave: (e) => { e.currentTarget.style.filter = 'none'; },
                        }
                        : {})}
                >
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {themeSettings.logoUrl ? (
                                <img src={themeSettings.logoUrl} alt="" style={{ width: '75%', height: '75%', objectFit: 'contain' }} />
                            ) : (
                                <ButtonIcon className="w-4 h-4" />
                            )}
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold">{agent?.name || 'Chat'}</h3>
                            <p className="text-xs text-white/70">{agent?.chat_header_subtitle || "We're here to help"}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isMinimized ? (
                            // The bar itself carries the click and the label. A real
                            // button nested inside a role="button" would be an
                            // accessibility trap and would double-toggle, so while
                            // minimized this is only the affordance.
                            <span className="p-1.5 rounded-lg" aria-hidden="true">
                                <Minimize2 className="w-4 h-4" />
                            </span>
                        ) : (
                            <button
                                onClick={handleMinimizeToggle}
                                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                                aria-label="Minimize"
                            >
                                <Minimize2 className="w-4 h-4" />
                            </button>
                        )}
                        {!isMinimized && (
                            <button
                                onClick={() => {
                                    // Ending the chat is now the only thing that closes the
                                    // backend session from the browser, alongside a "bye"
                                    // trigger and the inactivity auto-close. Leaving the page
                                    // no longer does — it carries the conversation over to the
                                    // next one instead (see chatSessionStore).
                                    const sid = readSessionId(agentId);
                                    if (sid && agentId) {
                                        api.post(`/agents/${agentId}/chat/embed/end?session_id=${sid}`).catch(() => {});
                                    }
                                    // Drop the stored conversation so it is not resumed on the
                                    // next page: the visitor asked for it to be over.
                                    clearAll(agentId);
                                    resetVerdict(agentId);
                                    // Force widget remount to clear messages
                                    widgetKeyRef.current = Date.now();
                                    setIsOpen(false);
                                    setIsMinimized(false);
                                }}
                                className="px-3 py-1.5 hover:bg-white/10 rounded-lg transition-colors text-sm font-medium"
                                aria-label="End Chat"
                            >
                                End Chat
                            </button>
                        )}
                    </div>
                </div>

                {/* Chat Content - Always render but hide when minimized to preserve state */}
                <div className={`flex-1 min-h-0 overflow-hidden flex flex-col ${isMinimized ? 'hidden' : ''}`}>
                    <div className="flex-1 min-h-0 h-full">
                    <ChatEmbedWidget
                        key={widgetKeyRef.current || 'chat-widget'}
                        agentId={agentId}
                        themeOverride={{
                            mode: themeSettings.mode,
                            primaryColor: themeSettings.primaryColor,
                            buttonColor: themeSettings.buttonColor,
                        }}
                        onClose={() => {
                            setIsOpen(false);
                            setIsMinimized(false);
                            widgetKeyRef.current = 0;
                        }} 
                    />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatEmbedButton;

